import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CadreAcces, EnTeteAcces } from '../components/CadreAcces';
import { Message } from '../components/charte/Elements';
import { cheminDeRetour } from '../auth/session';
import { messageConnexion } from '../utils/messagesErreur';
import RenvoiConfirmation from '../components/RenvoiConfirmation';

const Login: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const etat = (location.state ?? {}) as { retour?: string; expiree?: boolean; compteCree?: string };
  // Page demandée avant la connexion : état de navigation, ou ?retour= après un 401.
  const retour = cheminDeRetour(etat.retour ?? params.get('retour'));
  const expiree = !!etat.expiree || params.get('expiree') === '1';
  const [email, setEmail] = useState(etat.compteCree ?? '');
  const [nonConfirmee, setNonConfirmee] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNonConfirmee(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate(retour, { replace: true });
    } catch (err: any) {
      setError(messageConnexion(err));
      // 403 : adresse pas encore confirmée (comptes créés avant les invitations).
      setNonConfirmee(err?.response?.status === 403);
    } finally {
      setLoading(false);
    }
  };

  // Déjà connecté (autre onglet, retour arrière) : pas de second formulaire.
  if (isAuthenticated && !loading) return <Navigate to={retour} replace />;

  return (
    <CadreAcces>
      <EnTeteAcces etiquette="Portail client" titre="Connexion" />

      {etat.compteCree && !error && (
        <Message genre="succes">
          Votre compte est créé. Connectez-vous avec votre adresse {etat.compteCree} et le mot de passe choisi.
        </Message>
      )}
      {expiree && !error && (
        <Message genre="info">Votre session a expiré. Reconnectez-vous pour reprendre où vous en étiez.</Message>
      )}
      {error && <Message genre="erreur">{error}</Message>}
      {nonConfirmee && <RenvoiConfirmation emailInitial={email} id="connexion-renvoi-email" />}

      <form onSubmit={handleSubmit} className="pile" style={{ gap: '22px' }}>
        <div className="pile" style={{ gap: '14px' }}>
          <div className="champ">
            <label htmlFor="connexion-email">Adresse e-mail</label>
            <input
              id="connexion-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="prenom.nom@organisation.sn"
              required
            />
          </div>

          <div className="champ">
            <div className="ligne ligne--entre" style={{ alignItems: 'baseline' }}>
              <label htmlFor="connexion-mdp">Mot de passe</label>
              <Link to={`/mot-de-passe-oublie${email ? `?email=${encodeURIComponent(email)}` : ''}`} style={{ fontSize: '13px', fontWeight: 600 }}
                aria-label="Mot de passe oublié ?">Oublié ?</Link>
            </div>
            <input
              id="connexion-mdp"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••••••"
              required
            />
          </div>
        </div>

        <button type="submit" className="bouton bouton--large" disabled={loading}>
          {loading ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>

      <div className="carte-acces__pied">
        <p>
          L'accès au portail se fait <Link to="/inscription" style={{ fontWeight: 600 }}>sur invitation</Link> de Tribe. Vous avez reçu un
          lien ? Il ouvre directement la création de votre compte.
        </p>
        <p>
          Pas encore client ? <a href="mailto:missions@tribe.sn" style={{ fontWeight: 600 }}>Écrivez à missions@tribe.sn</a>
        </p>
      </div>
    </CadreAcces>
  );
};

export default Login;
